import * as signalR from "@microsoft/signalr";

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnecting = false;
    this._pendingListeners = [];
  }

  buildConnection() {
    if (this.connection) return;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(process.env.VUE_APP_SIGNALR_URL, {
        accessTokenFactory: () => localStorage.getItem("TOKEN")
      })
      .withAutomaticReconnect([0, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.registerConnectionEvents();

    // Flush any listeners registered before the connection was built
    this._pendingListeners.forEach(({ eventName, callback }) => {
      this.connection.on(eventName, callback);
    });
    this._pendingListeners = [];
  }

  registerConnectionEvents() {
    if (!this.connection) return;

    this.connection.onreconnecting((error) => {
      console.warn("SignalR reconnecting...", error);
    });

    this.connection.onreconnected(() => {
      console.log("SignalR reconnected.");
    });

    this.connection.onclose((error) => {
      console.error("SignalR connection closed.", error);
    });
  }

  async start() {
    if (process.env.VUE_APP_AI_MODE !== "true") return;

    if (!localStorage.getItem("TOKEN")) {
      console.warn("No TOKEN found. SignalR will not connect.");
      return;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    if (this.isConnecting) return;

    try {
      this.isConnecting = true;
      this.buildConnection();
      await this.connection.start();
      console.log("SignalR Connected.");
    } catch (error) {
      console.error("SignalR Start Error:", error);
    } finally {
      this.isConnecting = false;
    }
  }

  async stop() {
    if (!this.connection) return;

    try {
      await this.connection.stop();
      console.log("SignalR Disconnected.");
    } catch (error) {
      console.error("SignalR Stop Error:", error);
    }
  }

  on(eventName, callback) {
    if (!this.connection) {
      // Connection not built yet — queue and register once ready
      this._pendingListeners.push({ eventName, callback });
      return;
    }
    this.connection.on(eventName, callback);
  }

  off(eventName, callback) {
    if (!this.connection) return;
    this.connection.off(eventName, callback);
  }

  async invoke(methodName, payload) {
    if (!this.connection) return;

    try {
      return await this.connection.invoke(methodName, payload);
    } catch (error) {
      console.error(`SignalR Invoke Error [${methodName}]`, error);
    }
  }
}

export default new SignalRService();